import classNames from 'classnames'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignResultsStatusBadge } from '@pages/Traders-Room/Campaign-Results/components/CampaignResultsStatusBadge'

import { AuthSessionContext } from '@context/AuthContext'

import {
  Table,
  TableBody,
  TableCell,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { formatDate } from '@utils/date.utils'
import { getCentralRowIdConditionally } from '@utils/getItemId'

import { ClientRebateCampaignDto } from '@model/CampaignResultDto'

import styles from './CampaignResultsTable.module.scss'

interface Props {
  campaignResults: ClientRebateCampaignDto[]
  setSelectedCampaign(campaignResult: ClientRebateCampaignDto): void
  isLoading?: boolean
  sticky?: 'left'
}

export const CampaignResultsTable: React.FC<Props> = (props) => {
  const { campaignResults, setSelectedCampaign, isLoading, sticky } = props

  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)

  const dateFormat = auth?.dateFormatType?.name

  return (
    <Table
      isLoading={isLoading}
      sticky={sticky}
      noResults={!campaignResults.length && <TableNoResults title={t('No results found')} />}
    >
      <TableHeader>
        <TableRow>
          <TableHeadSort>{t('Campaigns.Campaign name')}</TableHeadSort>
          <TableHeadSort align='center' className={styles.column}>
            {t('Campaigns.Campaign Account No')}
          </TableHeadSort>
          <TableHeadSort align='center' className={styles.column}>
            {t('Campaigns.Start Date')}
          </TableHeadSort>
          <TableHeadSort align='center' className={styles.column}>
            {t('Campaigns.End Date')}
          </TableHeadSort>
          <TableHeadSort align='center' className={styles.column}>
            {t('Campaigns.Currency')}
          </TableHeadSort>
          <TableHeadSort align='center' className={styles.column}>
            <span className='is-flex is-justify-content-center'>{t('Campaigns.Status')}</span>
          </TableHeadSort>
          <TableHeadSort align='center' className={styles.column}>
            <span className='is-flex is-justify-content-center'>{t('Campaigns.Results')}</span>
          </TableHeadSort>
        </TableRow>
      </TableHeader>

      <TableBody>
        {campaignResults.map((x, index) => (
          <TableRow
            key={`${x.account}-${x.from}-${index}`}
            {...getCentralRowIdConditionally(index, campaignResults.length, 'campaign-results')}
          >
            <TableCell>{x.name}</TableCell>
            <TableCell align='center'>{x.account && <CampaignAccountNo {...x} />}</TableCell>
            <TableCell align='center'>
              {formatDate(x.from, { formatType: dateFormat, withTime: false })}
            </TableCell>
            <TableCell align='center'>
              {formatDate(x.to, { formatType: dateFormat, withTime: false })}
            </TableCell>
            <TableCell align='center'>{x.currency.id}</TableCell>
            <TableCell align='center'>
              <CampaignResultsStatusBadge {...x} />
            </TableCell>
            <TableCell align='center'>
              {!x.internalOnly ? (
                <span className='is-link' onClick={() => setSelectedCampaign(x)}>
                  {t('View')}
                </span>
              ) : (
                <span>{t('N/A')}</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const CampaignAccountNo: React.FC<ClientRebateCampaignDto> = (props) => {
  if (props.disqualified) {
    return <span className={classNames(styles.strikethrough)}>{getCampaignAccountNo(props)}</span>
  }

  return <span>{getCampaignAccountNo(props)}</span>
}

const getCampaignAccountNo = (campaign: ClientRebateCampaignDto) => {
  return `${campaign.account}`
}
